import React, { useMemo, useState, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  flexRender,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table";
import { ChevronDown, ChevronRight, Edit, History, Trash2 } from "lucide-react";
import { Button } from "../../atoms/Button";
import { Input } from "../../atoms/Input";
import { Modal } from "../Modal";
import { RevisionHistoryPanel } from "../RevisionHistoryPanel";
import type {
  FinancialDataGridProps,
  FinancialDataRow,
} from "./FinancialDataGrid.types";
import {
  useDeleteExpense,
  useDeleteRevenue,
  useDeleteEvent,
  useUndoExpenseRevision,
  useUndoRevenueRevision,
  useUndoAssetRevision,
} from "../../../hooks/useFinancialData";
import { formatCalendarDate, parseCalendarDate } from "../../../utils/date";
import {
  buildSegmentChains,
  findSegmentChain,
  getSegmentGroupId,
  type SegmentChain,
} from "../../../utils/chart/segment-grouping.utils";
import { getOpenSegmentTarget } from "../../../utils/chart/line-item-revision.utils";

const columnHelper = createColumnHelper<FinancialDataRow>();

const FinancialDataGrid: React.FC<FinancialDataGridProps> = ({
  startupId,
  expenses,
  revenues,
  events,
  onRowDelete,
  onExpenseClick,
  onRevenueClick,
  onEventClick,
  onRevisionReviseAgain,
  className = "",
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [historyChain, setHistoryChain] = useState<SegmentChain | null>(null);

  const segmentChains = useMemo(
    () => buildSegmentChains({ expenses, revenues, events }),
    [expenses, revenues, events],
  );

  const toDate = (d: unknown): Date | null => parseCalendarDate(d as Date | string);

  const formatDateSafe = (d: unknown) => formatCalendarDate(d as Date | string);

  // Mutations for updating data (kept for delete functionality)
  const deleteExpenseMutation = useDeleteExpense(startupId);
  const deleteRevenueMutation = useDeleteRevenue(startupId);
  const deleteEventMutation = useDeleteEvent(startupId);
  const undoExpenseRevision = useUndoExpenseRevision(startupId);
  const undoRevenueRevision = useUndoRevenueRevision(startupId);
  const undoAssetRevision = useUndoAssetRevision(startupId);

  const toggleGroupExpanded = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // Transform data into grid rows
  const data: FinancialDataRow[] = useMemo(() => {
    const rows: FinancialDataRow[] = [];

    // Add revenues
    revenues.forEach((revenue) => {
      const groupId = getSegmentGroupId(revenue);
      const chain = findSegmentChain(segmentChains, groupId);
      const openSegment = getOpenSegmentTarget(revenue, revenues);
      rows.push({
        id: revenue.id,
        type: "revenue",
        name: revenue.name,
        amount: revenue.amount || revenue.monthlyAmount,
        category: (revenue as any).category || revenue.type,
        startDate: revenue.startDate,
        endDate: revenue.endDate,
        frequency: revenue.frequency,
        description: revenue.description,
        lineItemGroupId: groupId,
        segmentKind: revenue.segmentKind,
        isHistoricalSegment: Boolean(revenue.endDate),
        chainSegmentCount: chain?.segments.length,
        showChainToggle: openSegment?.id === revenue.id && (chain?.segments.length ?? 0) > 1,
        originalData: revenue,
      });
    });

    // Add expenses
    expenses.forEach((expense) => {
      let amount: number | undefined;
      if (expense.isOneTime) {
        amount = expense.oneTimeAmount;
      } else {
        amount = expense.amount || expense.monthlyAmount;
      }

      const groupId = getSegmentGroupId(expense);
      const chain = findSegmentChain(segmentChains, groupId);
      const openSegment = getOpenSegmentTarget(expense, expenses);

      rows.push({
        id: expense.id,
        type: "expense",
        name: expense.name,
        amount: amount,
        category: expense.category,
        startDate: expense.startDate,
        endDate: expense.endDate,
        frequency: expense.isOneTime ? "one-time" : "monthly",
        expenseType: expense.expenseType,
        lineItemGroupId: groupId,
        segmentKind: expense.segmentKind,
        isHistoricalSegment: Boolean(expense.endDate),
        chainSegmentCount: chain?.segments.length,
        showChainToggle: openSegment?.id === expense.id && (chain?.segments.length ?? 0) > 1,
        originalData: expense,
      });
    });

    // Add events
    events.forEach((event) => {
      const groupId =
        event.type === "asset" ? getSegmentGroupId(event) : event.id;
      const chain =
        event.type === "asset" ? findSegmentChain(segmentChains, groupId) : undefined;
      const openSegment =
        event.type === "asset"
          ? getOpenSegmentTarget(
              { ...event, startDate: event.date },
              events
                .filter((candidate) => candidate.type === "asset")
                .map((candidate) => ({ ...candidate, startDate: candidate.date })),
            )
          : null;

      const row = {
        id: event.id,
        name: event.description ?? "(no description)",
        date: event.date,
        type: "event" as const,
        category: event.type || undefined,
        amount: (event as any).amount,
        color: event.color || "#000",
        lineItemGroupId: event.type === "asset" ? groupId : undefined,
        segmentKind: event.segmentKind,
        isHistoricalSegment: Boolean(event.endDate),
        chainSegmentCount: chain?.segments.length,
        showChainToggle:
          event.type === "asset" &&
          openSegment?.id === event.id &&
          (chain?.segments.length ?? 0) > 1,
        originalData: event as any,
      };
      rows.push(row as any);
    });

    // Sort by type priority (Revenue -> Expense -> Event) then by name
    return rows.sort((a, b) => {
      const typePriority = { revenue: 0, expense: 1, event: 2 };
      const typeCompare = typePriority[a.type] - typePriority[b.type];
      if (typeCompare !== 0) return typeCompare;
      return a.name.localeCompare(b.name);
    });
  }, [expenses, revenues, events, segmentChains]);

  const visibleData = useMemo(() => {
    return data.filter((row) => {
      if (!row.isHistoricalSegment || !row.lineItemGroupId) return true;
      return expandedGroups.has(row.lineItemGroupId);
    });
  }, [data, expandedGroups]);

  // Handle row editing
  const handleRowEdit = useCallback((rowId: string) => {
    const row = data.find((r) => r.id === rowId);
    if (!row) return;

    if (row.type === "expense" && onExpenseClick) {
      onExpenseClick(row.id);
    } else if (row.type === "revenue" && onRevenueClick) {
      onRevenueClick(row.id);
    } else if (row.type === "event" && onEventClick) {
      onEventClick(row.id);
    }
  }, [data, onExpenseClick, onRevenueClick, onEventClick]);

  const handleOpenHistory = useCallback((groupId: string) => {
    const chain = findSegmentChain(segmentChains, groupId);
    if (chain) setHistoryChain(chain);
  }, [segmentChains]);

  const handleUndoLatest = useCallback(async (latestRevisionId: string) => {
    if (!historyChain) return;
    try {
      if (historyChain.kind === "expense") {
        await undoExpenseRevision.mutateAsync(latestRevisionId);
      } else if (historyChain.kind === "revenue") {
        await undoRevenueRevision.mutateAsync(latestRevisionId);
      } else {
        await undoAssetRevision.mutateAsync(latestRevisionId);
      }
      setHistoryChain(null);
    } catch (error) {
      console.error("Failed to undo revision:", error);
    }
  }, [historyChain, undoExpenseRevision, undoRevenueRevision, undoAssetRevision]);

  // Handle row deletion
  const handleRowDelete = async (rowId: string) => {
    const row = data.find((r) => r.id === rowId);
    if (!row) return;

    if (
      !confirm(
        `Are you sure you want to delete this ${row.type}: "${row.name}"?`
      )
    ) {
      return;
    }

    try {
      if (row.type === "expense") {
        await deleteExpenseMutation.mutateAsync(row.id);
      } else if (row.type === "revenue") {
        await deleteRevenueMutation.mutateAsync(row.id);
      } else if (row.type === "event") {
        await deleteEventMutation.mutateAsync(row.id);
      }
      onRowDelete?.(row);
    } catch (error) {
      console.error("Error deleting data:", error);
    }
  };

  // Define columns
  const columns = useMemo(
    () => [
      columnHelper.accessor("type", {
        header: "Type",
        cell: ({ getValue }) => {
          const value = getValue();
          const colors = {
            revenue: "bg-brand/15 text-emerald-100",
            expense: "bg-expense/15 text-[#e2cdd8]",
            event: "bg-brand/15 text-emerald-100",
          };
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${colors[value]}`}
            >
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </span>
          );
        },
        size: 100,
      }),
      columnHelper.accessor("name", {
        header: "Name",
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-2">
            {row.original.showChainToggle ? (
              <button
                type="button"
                className="text-white/50 hover:text-white"
                onClick={() => {
                  if (row.original.lineItemGroupId) {
                    toggleGroupExpanded(row.original.lineItemGroupId);
                  }
                }}
                title="Show segment history"
              >
                {row.original.lineItemGroupId &&
                expandedGroups.has(row.original.lineItemGroupId) ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </button>
            ) : row.original.isHistoricalSegment ? (
              <span className="inline-block w-4" />
            ) : null}
            <span
              className={`font-medium ${
                row.original.isHistoricalSegment ? "text-white/55" : ""
              }`}
            >
              {getValue()}
            </span>
            {row.original.segmentKind === "revision" ? (
              <span className="rounded-full bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-200">
                revised
              </span>
            ) : null}
            {row.original.chainSegmentCount && row.original.chainSegmentCount > 1 ? (
              <span className="text-[10px] text-white/40">
                {row.original.chainSegmentCount} segments
              </span>
            ) : null}
          </div>
        ),
        size: 240,
      }),
      columnHelper.accessor("amount", {
        header: "Amount",
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (row.original.type === "event") {
            // Events may have an amount (e.g., funding). Show it when present.
            const evtAmount = (row.original as any).amount;
            return evtAmount || evtAmount === 0 ? (
              <span className="font-mono">${Number(evtAmount).toLocaleString()}</span>
            ) : (
              <span className="text-white/40">—</span>
            );
          }

          return (
            <span className="font-mono">
              ${value?.toLocaleString() || "0"}
            </span>
          );
        },
        size: 120,
      }),
      columnHelper.accessor("category", {
        header: "Category",
        cell: ({ getValue }) => (
          <span className="capitalize">{getValue()}</span>
        ),
        size: 150,
      }),
      columnHelper.accessor("frequency", {
        header: "Frequency",
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (row.original.type === "event") {
            return <span className="text-white/40">—</span>;
          }
          return <span className="capitalize">{value}</span>;
        },
        size: 120,
      }),
      columnHelper.accessor("expenseType", {
        header: "Expense Type",
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (row.original.type !== "expense") {
            return <span className="text-white/40">—</span>;
          }
          return <span className="capitalize">{value}</span>;
        },
        size: 120,
      }),
      columnHelper.accessor("startDate", {
        header: "Start Date",
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (row.original.type === "event") {
            // For events, show the main date in the start date column
            const eventDate = toDate((row.original as any).date);
            return <span>{formatDateSafe(eventDate)}</span>;
          }
          const sd = toDate(value);
          return <span>{formatDateSafe(sd)}</span>;
        },
        size: 120,
      }),
      columnHelper.accessor("endDate", {
        header: "End Date",
        cell: ({ getValue, row }) => {
          const value = getValue();
          if (row.original.type === "event") {
            return <span className="text-white/40">—</span>;
          }
          const ed = toDate(value);
          return <span>{ed ? formatDateSafe(ed) : "—"}</span>;
        },
        size: 120,
      }),
      columnHelper.accessor("description", {
        header: "Description",
        cell: ({ getValue }) => (
          <span className="text-white/65 truncate block max-w-[200px]">
            {getValue() || "—"}
          </span>
        ),
        size: 200,
      }),
      columnHelper.display({
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            {row.original.chainSegmentCount && row.original.chainSegmentCount > 1 ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (row.original.lineItemGroupId) {
                    handleOpenHistory(row.original.lineItemGroupId);
                  }
                }}
                className="text-sky-300 hover:bg-sky-500/10"
                aria-label="Revision history"
              >
                <History className="h-4 w-4" />
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleRowEdit(row.original.id)}
              className="text-brand hover:bg-brand/10"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleRowDelete(row.original.id)}
              className="text-danger hover:bg-danger/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
        size: 150,
      }),
    ],
    [
      expandedGroups,
      handleOpenHistory,
      handleRowEdit,
      handleRowDelete,
      toggleGroupExpanded,
    ],
  );

  const table = useReactTable({
    data: visibleData,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className={`sh-panel overflow-hidden ${className}`}>
      {/* Header with search */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">
            Financial Data Grid
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-white/65">
              {table.getFilteredRowModel().rows.length} items
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Input
            placeholder="Search all data..."
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className="max-w-sm"
          />
          <div className="flex items-center gap-2 text-sm text-white/65">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-brand/15 border border-brand/30 rounded"></span>
              Revenues: {revenues.length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-danger/15 border border-danger/30 rounded"></span>
              Expenses: {expenses.length}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-brand/15 border border-focus/30 rounded"></span>
              Events: {events.length}
            </span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="border-b border-white/10">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left text-xs font-medium text-white/45 uppercase tracking-wider cursor-pointer hover:bg-surface-elevated"
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    <div className="flex items-center gap-1">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getIsSorted() && (
                        <span className="text-brand">
                          {header.column.getIsSorted() === "desc" ? "↓" : "↑"}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-white/10">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-surface-elevated">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 py-3 whitespace-nowrap text-sm text-white"
                  >
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext()
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty state */}
      {table.getFilteredRowModel().rows.length === 0 && (
        <div className="p-8 text-center">
          <div className="text-white/45">
            {globalFilter
              ? "No data matches your search."
              : "No financial data available."}
          </div>
        </div>
      )}

      {historyChain ? (
        <Modal
          isOpen={Boolean(historyChain)}
          onClose={() => setHistoryChain(null)}
          title={`Revision history — ${historyChain.label}`}
        >
          <RevisionHistoryPanel
            chain={historyChain}
            onClose={() => setHistoryChain(null)}
            onViewSegment={(segmentId, kind) => {
              setHistoryChain(null);
              if (kind === "expense") onExpenseClick?.(segmentId);
              else if (kind === "revenue") onRevenueClick?.(segmentId);
              else onEventClick?.(segmentId);
            }}
            onReviseAgain={
              onRevisionReviseAgain
                ? () => {
                    const open = getOpenSegmentTarget(
                      {
                        id: historyChain.segments[historyChain.segments.length - 1]?.id,
                        lineItemGroupId: historyChain.groupId,
                        startDate:
                          historyChain.segments[historyChain.segments.length - 1]
                            ?.startDate,
                        endDate:
                          historyChain.segments[historyChain.segments.length - 1]
                            ?.endDate,
                      },
                      historyChain.segments.map((segment) => ({
                        id: segment.id,
                        lineItemGroupId: historyChain.groupId,
                        startDate: segment.startDate,
                        endDate: segment.endDate,
                      })),
                    );
                    if (!open) return;
                    onRevisionReviseAgain({
                      kind: historyChain.kind,
                      segmentId: open.id,
                      groupId: historyChain.groupId,
                    });
                    setHistoryChain(null);
                  }
                : undefined
            }
            onUndoLatest={handleUndoLatest}
            isUndoing={
              undoExpenseRevision.isPending ||
              undoRevenueRevision.isPending ||
              undoAssetRevision.isPending
            }
          />
        </Modal>
      ) : null}
    </div>
  );
};

export default FinancialDataGrid;